/**
 * SightCardZoomModal — click-to-zoom full-size overlay for SIGHT cards.
 *
 * Reuses the data URI already lazy-loaded by SightCard (so no second
 * retrieval op). Image cards show the full data URI at viewport size;
 * text-only cards show the full `item.content` (not the 200-char
 * display excerpt). Dismisses on backdrop click + Escape key.
 *
 * Mounted by SightImageGrid via lifted `expandedItemId` state.
 *
 * Pattern: mirrors VoiceSettingsModal (apps/vibe-designer/frontend/src/
 * components/voice/VoicePanel.tsx) — fixed inset-0 backdrop with onClick=
 * onClose; inner panel uses e.stopPropagation() to absorb clicks; Escape
 * key listener attached at window scope while modal is mounted.
 */
import { useEffect } from 'react';
import type { SightRecentItem } from '@/services/knowledgeApi';

interface SightCardZoomModalProps {
  item: SightRecentItem;
  imageUrl: string | null;
  onClose: () => void;
}

export function SightCardZoomModal({ item, imageUrl, onClose }: SightCardZoomModalProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const isImage = item.has_image && imageUrl;

  return (
    <div
      data-testid="sight-card-zoom-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        data-testid="sight-card-zoom-panel"
        className="relative bg-vibe-bg-secondary border border-vibe-border rounded shadow-xl max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-3 py-2 border-b border-vibe-border">
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-vibe-text truncate" title={item.source_file || item.id}>
              {item.source_file || item.id}
            </span>
            <span className="text-[10px] text-vibe-text-secondary tabular-nums">
              {item.embedding_type} · {item.has_image ? 'image' : 'text'}
              {item.updated_at && ` · ${item.updated_at}`}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close zoom modal"
            className="text-vibe-text-secondary hover:text-vibe-text text-lg px-2 leading-none"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-auto bg-vibe-bg p-3">
          {isImage ? (
            <img
              data-testid="sight-card-zoom-img"
              src={imageUrl!}
              alt={item.source_file || 'SIGHT image'}
              className="max-w-full max-h-[80vh] object-contain mx-auto"
            />
          ) : (
            <pre
              data-testid="sight-card-zoom-content"
              className="text-xs text-vibe-text whitespace-pre-wrap font-mono leading-relaxed"
            >
              {item.content}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
